import { ChangeEvent, DragEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    BusinessCaseAttachmentPayload,
    BusinessCaseCategoryId,
    BusinessCaseDraftState,
    BusinessCasePayload,
    BusinessCaseRegistrationPayload,
    createBusinessCase,
    createBusinessCaseRegistration,
    getBusinessCaseDraft,
    saveBusinessCaseDraft,
} from "../api/businessCase";
import Logo from "../assets/logo_text.png";
import { useLanguage } from "../i18n/language.context";
import { apiErrorKey } from "../i18n/errorMessages";
import "./businessCase.page.css";
import Turnstile from "../components/turnstile.component";

type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

type GuideSection = {
    title: string;
    description: string;
    titleKey: string;
    descriptionKey: string;
};

type BusinessCategory = {
    id: BusinessCaseCategoryId;
    title: string;
    description: string;
    titleKey: string;
    descriptionKey: string;
    icon: "partnership" | "campaign" | "service";
    sections: GuideSection[];
};

type RegistrationFormData = {
    companyName: string;
    vatId: string;
    streetAddress: string;
    postalCode: string;
    city: string;
    country: string;
    generalPhone: string;
    generalEmail: string;
    differentBillingAddress: string;
    billingCompanyName: string;
    billingStreetAddress: string;
    billingPostalCode: string;
    billingCity: string;
    billingCountry: string;
    contactFirstName: string;
    contactLastName: string;
    salutation: string;
    contactPhone: string;
    contactEmail: string;
    brandPartnershipCount: string;
    campaignCount: string;
    serviceProviderCount: string;
    netPricesAccepted: boolean;
    conditionsAccepted: boolean;
    legallyBindingOfferAccepted: boolean;
    cancellationAccepted: boolean;
    privacyAccepted: boolean;
};

type CaseData = {
    title: string;
    shortDescription: string;
    challenge: string;
    objectives: string;
    execution: string;
    impactMetrics: string;
    strategicValue: string;
};

type ValidationIssue = {
    field: string;
    message: string;
    step: WizardStep;
};

type UploadedFileItem = {
    id: string;
    file: File | null;
    name: string;
    size: number;
    extension: string;
    restored?: boolean;
};

type SubmittedBusinessCase = {
    draftCaseKey: string;
    backendCaseId: string;
    backendRegistrationId: string;
    submissionId: string;
    submittedAt: string;
    categoryId: BusinessCaseCategoryId;
    categoryTitle: string;
    caseTitle: string;
};

type StoredBusinessCaseWizardState = {
    currentStep: WizardStep;
    selectedCategoryId: BusinessCaseCategoryId;
    registrationData: RegistrationFormData;
    caseData: CaseData;
    uploadedFiles: Array<{
        id: string;
        name: string;
        size: number;
        extension: string;
    }>;
    draftCaseKey: string;
    submittedCases: SubmittedBusinessCase[];
    backendRegistrationId: string;
    submissionId: string;
    submissionDate: string;
    draftToken: string;
};

const categories: BusinessCategory[] = [
    {
        id: "brand-partnership",
        title: "Brand Partnership of the Year",
        titleKey: "bc.cat.brand.title",
        description:
            "Celebrate the most impactful collaboration between brands by recognizing partnerships that elevated creativity, reach, and cultural relevance.",
        descriptionKey: "bc.cat.brand.description",
        icon: "partnership",
        sections: [
            {
                title: "Basic Information",
                titleKey: "bc.cat.brand.s1.title",
                description:
                    "Provide the partnership name, participating brands or organizations, duration, and targeted markets or regions.",
                descriptionKey: "bc.cat.brand.s1.description",
            },
            {
                title: "Strategic Context",
                titleKey: "bc.cat.brand.s2.title",
                description:
                    "Describe the partnership objective, the challenge or opportunity it addressed, and the target audience it was built for.",
                descriptionKey: "bc.cat.brand.s2.description",
            },
            {
                title: "Partnership Structure",
                titleKey: "bc.cat.brand.s3.title",
                description:
                    "Explain the structure, scope, and roles involved, for example licensing, co-branding, distribution, retail, event, or creator collaborations.",
                descriptionKey: "bc.cat.brand.s3.description",
            },
            {
                title: "Impact Metrics",
                titleKey: "bc.cat.brand.s4.title",
                description:
                    "Show revenue, reach, participation, engagement, earned media, or other metrics that prove the impact of the partnership.",
                descriptionKey: "bc.cat.brand.s4.description",
            },
            {
                title: "Strategic Value",
                titleKey: "bc.cat.brand.s5.title",
                description:
                    "Highlight how the partnership creates long-term value for the brands, the game, and the broader TCG ecosystem.",
                descriptionKey: "bc.cat.brand.s5.description",
            },
        ],
    },
    {
        id: "campaign-of-the-year",
        title: "Campaign of the Year",
        titleKey: "bc.cat.campaign.title",
        description:
            "Recognize the most outstanding marketing campaign in the TCG industry with a strong combination of strategy, storytelling, and measurable impact.",
        descriptionKey: "bc.cat.campaign.description",
        icon: "campaign",
        sections: [
            {
                title: "Basic Information",
                titleKey: "bc.cat.campaign.s1.title",
                description:
                    "Provide the campaign name, participating brands or organizations, duration, and targeted markets or regions.",
                descriptionKey: "bc.cat.campaign.s1.description",
            },
            {
                title: "Campaign Objectives",
                titleKey: "bc.cat.campaign.s2.title",
                description:
                    "Explain why the campaign was launched, which audience it targeted, and what the core creative concept was.",
                descriptionKey: "bc.cat.campaign.s2.description",
            },
            {
                title: "Campaign Execution",
                titleKey: "bc.cat.campaign.s3.title",
                description:
                    "Describe measures, channels, creator partnerships, events, retail activations, and content formats used.",
                descriptionKey: "bc.cat.campaign.s3.description",
            },
            {
                title: "Impact Metrics",
                titleKey: "bc.cat.campaign.s4.title",
                description:
                    "Share business impact, reach, participation, engagement, media coverage, and other success metrics.",
                descriptionKey: "bc.cat.campaign.s4.description",
            },
            {
                title: "Strategic Value",
                titleKey: "bc.cat.campaign.s5.title",
                description:
                    "Describe the campaign's long-term impact and how it created value for the TCG player and fan base.",
                descriptionKey: "bc.cat.campaign.s5.description",
            },
        ],
    },
    {
        id: "service-provider",
        title: "Service Provider of the Year",
        titleKey: "bc.cat.service.title",
        description:
            "Celebrate the platform, app, or service that made life easier for players, stores, and organizers and strengthened the TCG ecosystem.",
        descriptionKey: "bc.cat.service.description",
        icon: "service",
        sections: [
            {
                title: "Basic Information",
                titleKey: "bc.cat.service.s1.title",
                description:
                    "Name the service, platform, participating brands or organizations, and the markets or regions it serves.",
                descriptionKey: "bc.cat.service.s1.description",
            },
            {
                title: "Service Overview",
                titleKey: "bc.cat.service.s2.title",
                description:
                    "Describe the core functionality, active category, target audience, and which industry problem the service solves.",
                descriptionKey: "bc.cat.service.s2.description",
            },
            {
                title: "Product and Execution",
                titleKey: "bc.cat.service.s3.title",
                description:
                    "Explain features, integrations, scalability, reliability, and how customer support or service delivery works.",
                descriptionKey: "bc.cat.service.s3.description",
            },
            {
                title: "Adoption, Usage, and Impact Metrics",
                titleKey: "bc.cat.service.s4.title",
                description:
                    "Show how success is measured, how many people use the service, and what business impact it creates.",
                descriptionKey: "bc.cat.service.s4.description",
            },
            {
                title: "Strategic Value",
                titleKey: "bc.cat.service.s5.title",
                description:
                    "Highlight long-term relevance, innovation, differentiation, and why the service matters for the wider industry.",
                descriptionKey: "bc.cat.service.s5.description",
            },
        ],
    },
];

const wizardStepKeys = [
    { id: 1 as WizardStep, titleKey: "bc.step.overview.title", subtitleKey: "bc.step.overview.subtitle" },
    { id: 2 as WizardStep, titleKey: "bc.step.registration.title", subtitleKey: "bc.step.registration.subtitle" },
    { id: 3 as WizardStep, titleKey: "bc.step.category.title", subtitleKey: "bc.step.category.subtitle" },
    { id: 4 as WizardStep, titleKey: "bc.step.case.title", subtitleKey: "bc.step.case.subtitle" },
    { id: 5 as WizardStep, titleKey: "bc.step.terms.title", subtitleKey: "bc.step.terms.subtitle" },
    { id: 6 as WizardStep, titleKey: "bc.step.attachments.title", subtitleKey: "bc.step.attachments.subtitle" },
    { id: 7 as WizardStep, titleKey: "bc.step.review.title", subtitleKey: "bc.step.review.subtitle" },
];

const countryOptions = [
    "Germany",
    "Austria",
    "Switzerland",
    "Netherlands",
    "Belgium",
    "France",
    "Italy",
    "Spain",
    "United Kingdom",
    "United States",
    "Canada",
    "Other",
];

const countryLabelKeys: Record<string, string> = {
    Germany: "bc.country.germany",
    Austria: "bc.country.austria",
    Switzerland: "bc.country.switzerland",
    Netherlands: "bc.country.netherlands",
    Belgium: "bc.country.belgium",
    France: "bc.country.france",
    Italy: "bc.country.italy",
    Spain: "bc.country.spain",
    "United Kingdom": "bc.country.uk",
    "United States": "bc.country.us",
    Canada: "bc.country.canada",
    Other: "bc.country.other",
};

const emptyRegistrationData: RegistrationFormData = {
    companyName: "",
    vatId: "",
    streetAddress: "",
    postalCode: "",
    city: "",
    country: "",
    generalPhone: "",
    generalEmail: "",
    differentBillingAddress: "no",
    billingCompanyName: "",
    billingStreetAddress: "",
    billingPostalCode: "",
    billingCity: "",
    billingCountry: "",
    contactFirstName: "",
    contactLastName: "",
    salutation: "",
    contactPhone: "",
    contactEmail: "",
    brandPartnershipCount: "0",
    campaignCount: "0",
    serviceProviderCount: "0",
    netPricesAccepted: false,
    conditionsAccepted: false,
    legallyBindingOfferAccepted: false,
    cancellationAccepted: false,
    privacyAccepted: false,
};

const emptyCaseData: CaseData = {
    title: "",
    shortDescription: "",
    challenge: "",
    objectives: "",
    execution: "",
    impactMetrics: "",
    strategicValue: "",
};

const acceptedFileExtensions = ["pdf", "ppt", "pptx", "jpg", "jpeg", "png", "mp4"];
const businessCaseStorageKey = "tcg-awards-business-case-flow";

function BusinessCasePage() {
    const { t: tr } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const registrationFormRef = useRef<HTMLFormElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const hydratedDraftTokenRef = useRef<string>("");
    const persistedState = useMemo(() => readStoredBusinessCaseWizardState(), []);
    const draftTokenFromSearch = parseDraftTokenFromSearch(location.search);

    const [currentStep, setCurrentStep] = useState<WizardStep>(
        () => parseStepFromSearch(location.search) ?? persistedState?.currentStep ?? 1
    );
    const [selectedCategoryId, setSelectedCategoryId] = useState<BusinessCaseCategoryId>(
        () => persistedState?.selectedCategoryId ?? "campaign-of-the-year"
    );
    const [registrationData, setRegistrationData] = useState<RegistrationFormData>(
        () => persistedState?.registrationData ?? emptyRegistrationData
    );
    const [caseData, setCaseData] = useState<CaseData>(() => persistedState?.caseData ?? emptyCaseData);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFileItem[]>(
        () =>
            persistedState?.uploadedFiles.map((uploadedFile) => ({
                ...uploadedFile,
                file: null,
                restored: true,
            })) ?? []
    );
    const [draftCaseKey, setDraftCaseKey] = useState<string>(
        () => persistedState?.draftCaseKey ?? createDraftCaseKey()
    );
    const [dragActive, setDragActive] = useState(false);
    const [stepError, setStepError] = useState("");
    const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
    const [submissionId, setSubmissionId] = useState<string>(() => persistedState?.submissionId ?? "");
    const [submissionDate, setSubmissionDate] = useState<string>(() => persistedState?.submissionDate ?? "");
    const [submittedCases, setSubmittedCases] = useState<SubmittedBusinessCase[]>(
        () => persistedState?.submittedCases ?? []
    );
    const [backendRegistrationId, setBackendRegistrationId] = useState<string>(
        () => persistedState?.backendRegistrationId ?? ""
    );
    const [draftToken, setDraftToken] = useState<string>(
        () => draftTokenFromSearch ?? persistedState?.draftToken ?? ""
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isLoadingDraft, setIsLoadingDraft] = useState(Boolean(draftTokenFromSearch));
    const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
    const [draftLink, setDraftLink] = useState("");
    const [draftFeedback, setDraftFeedback] = useState("");
        const wizardSteps = useMemo(
        () =>
            wizardStepKeys.map((step) => ({
                id: step.id,
                title: tr(step.titleKey),
                subtitle: tr(step.subtitleKey),
            })),
        [tr]
    );

    // Captcha für die finale Einreichung (Schritt 7).
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaReset, setCaptchaReset] = useState(0);

        const translateFallback = (key: string, fallback: string) => {
        const translated = tr(key);
        return translated === key ? fallback : translated;
    };

    const categoryTitle = (category: BusinessCategory) =>
        translateFallback(category.titleKey, category.title);

    const categoryDescription = (category: BusinessCategory) =>
        translateFallback(category.descriptionKey, category.description);

    const guideSectionTitle = (section: GuideSection) =>
        translateFallback(section.titleKey, section.title);

    const guideSectionDescription = (section: GuideSection) =>
        translateFallback(section.descriptionKey, section.description);

    const countryLabel = (country: string) =>
        translateFallback(countryLabelKeys[country] ?? country, country);

    const selectedCategory = useMemo(
        () => categories.find((category) => category.id === selectedCategoryId) ?? categories[1],
        [selectedCategoryId]
    );

    const totalCasesSelected = useMemo(
        () =>
            Number(registrationData.brandPartnershipCount) +
            Number(registrationData.campaignCount) +
            Number(registrationData.serviceProviderCount),
        [
            registrationData.brandPartnershipCount,
            registrationData.campaignCount,
            registrationData.serviceProviderCount,
        ]
    );

    const selectedCaseBreakdown = useMemo(() => {
        const items = [
            {
                label: tr("bc.cat.brand.short"),
                value: Number(registrationData.brandPartnershipCount),
            },
            {
                label: tr("bc.cat.campaign.short"),
                value: Number(registrationData.campaignCount),
            },
            {
                label: tr("bc.cat.service.short"),
                value: Number(registrationData.serviceProviderCount),
            },
        ].filter((item) => item.value > 0);

        if (items.length === 0) {
            return tr("bc.reg.noCasesSelected");
        }

        return items.map((item) => `${item.label}: ${item.value}`).join(" | ");
    }, [
        registrationData.brandPartnershipCount,
        registrationData.campaignCount,
        registrationData.serviceProviderCount,
        tr,
    ]);

    const selectedCategoryCount = useMemo(
        () => getCategoryCaseCount(selectedCategoryId, registrationData),
        [registrationData, selectedCategoryId]
    );

    const selectedCategorySubmittedCount = useMemo(
        () => countSubmittedCasesForCategory(selectedCategoryId, submittedCases),
        [selectedCategoryId, submittedCases]
    );

    const selectedCategoryRemainingCount = useMemo(
        () => Math.max(selectedCategoryCount - selectedCategorySubmittedCount, 0),
        [selectedCategoryCount, selectedCategorySubmittedCount]
    );

    const totalSubmittedCases = submittedCases.length;
    const remainingCases = Math.max(totalCasesSelected - totalSubmittedCases, 0);
    const caseCountLabel = (count: number) => (count === 1 ? tr("bc.common.case") : tr("bc.common.cases"));
    const fileCountLabel = (count: number) => (count === 1 ? tr("bc.attachments.file") : tr("bc.attachments.files"));

    const contactName = useMemo(
        () => [registrationData.contactFirstName, registrationData.contactLastName].filter(Boolean).join(" "),
        [registrationData.contactFirstName, registrationData.contactLastName]
    );

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });
    }, [currentStep]);

    useEffect(() => {
        const page = document.getElementById("business-case-page");

        if (!page) {
            return;
        }

        page.querySelectorAll<HTMLElement>("[aria-invalid='true']").forEach((element) => {
            element.removeAttribute("aria-invalid");
        });
        page.querySelectorAll<HTMLElement>("[data-validation-error]").forEach((element) => {
            element.removeAttribute("data-validation-error");
        });

        validationIssues
            .filter((issue) => issue.step === currentStep)
            .forEach((issue) => {
                const controls = page.querySelectorAll<HTMLElement>(
                    `[name="${issue.field}"], [data-validation-field="${issue.field}"]`
                );

                controls.forEach((control) => control.setAttribute("aria-invalid", "true"));

                const container = controls[0]?.closest<HTMLElement>(
                    ".business-case-field, .business-case-radio-group, .business-case-checkbox-card, .business-case-upload-wrapper, .business-case-category-grid"
                );

                if (container && !container.dataset.validationError) {
                    container.dataset.validationError = issue.message;
                }
            });
    }, [currentStep, validationIssues]);

    const syncWizardLocation = (
        nextStep: WizardStep,
        historyMode: "push" | "replace" = "push",
        nextDraftToken?: string | null
    ) => {
        const nextSearchParams = new URLSearchParams(location.search);

        if (nextStep === 1) {
            nextSearchParams.delete("step");
        } else {
            nextSearchParams.set("step", String(nextStep));
        }

        const tokenToWrite = nextDraftToken ?? draftToken;

        if (tokenToWrite) {
            nextSearchParams.set("draft", tokenToWrite);
        } else {
            nextSearchParams.delete("draft");
        }

        navigate(
            {
                pathname: location.pathname,
                search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
            },
            { replace: historyMode === "replace" }
        );
    };

    const focusValidationField = (field: string) => {
        window.setTimeout(() => {
            const element = document.querySelector<HTMLElement>(
                `#business-case-page [name="${field}"], #business-case-page [data-validation-field="${field}"]`
            );

            element?.scrollIntoView({ behavior: "smooth", block: "center" });
            element?.focus({ preventScroll: true });
        }, 50);
    };

    const navigateToValidationIssue = (issue: ValidationIssue) => {
        if (issue.step !== currentStep) {
            syncWizardLocation(issue.step, "replace");
        }

        focusValidationField(issue.field);
    };

    const clearValidationIssue = (field: string) => {
        setValidationIssues((currentIssues) => currentIssues.filter((issue) => issue.field !== field));
    };

    useEffect(() => {
        writeStoredBusinessCaseWizardState({
            currentStep,
            selectedCategoryId,
            registrationData,
            caseData,
            uploadedFiles: uploadedFiles.map((uploadedFile) => ({
                id: uploadedFile.id,
                name: uploadedFile.name,
                size: uploadedFile.size,
                extension: uploadedFile.extension,
            })),
            draftCaseKey,
            submittedCases,
            backendRegistrationId,
            submissionId,
            submissionDate,
            draftToken,
        });
    }, [
        backendRegistrationId,
        caseData,
        currentStep,
        draftCaseKey,
        draftToken,
        registrationData,
        selectedCategoryId,
        submissionDate,
        submissionId,
        submittedCases,
        uploadedFiles,
    ]);

    useEffect(() => {
        const stepFromUrl = parseStepFromSearch(location.search) ?? 1;

        if (stepFromUrl !== currentStep) {
            setCurrentStep(stepFromUrl);
            setStepError("");
        }
    }, [currentStep, location.search]);

    useEffect(() => {
        if (!draftTokenFromSearch || hydratedDraftTokenRef.current === draftTokenFromSearch) {
            return;
        }

        let isCancelled = false;

        hydratedDraftTokenRef.current = draftTokenFromSearch;
        setIsLoadingDraft(true);
        setDraftFeedback("");

        getBusinessCaseDraft(draftTokenFromSearch)
            .then((draft) => {
                if (isCancelled) {
                    return;
                }

                const resolvedDraftToken = draft.token ?? draftTokenFromSearch;

                setSelectedCategoryId(draft.selectedCategoryId);
                setRegistrationData(normalizeDraftRegistrationData(draft.registrationData));
                setCaseData(normalizeDraftCaseData(draft.caseData));
                setUploadedFiles(normalizeDraftUploadedFiles(draft.uploadedFiles));
                setSubmittedCases(normalizeDraftSubmittedCases(draft.submittedCases));
                setBackendRegistrationId(draft.backendRegistrationId ?? "");
                setSubmissionId(draft.submissionId ?? "");
                setSubmissionDate(draft.submissionDate ?? "");
                setDraftCaseKey(draft.draftCaseKey);
                setDraftToken(resolvedDraftToken);
                setDraftLink(buildDraftShareLink(resolvedDraftToken, location.pathname));
                setCurrentStep(clampDraftStep(draft.currentStep));
                setStepError("");
                setValidationIssues([]);
                setDraftFeedback("");

                const nextSearchParams = new URLSearchParams(location.search);
                nextSearchParams.set("step", String(clampDraftStep(draft.currentStep)));
                nextSearchParams.set("draft", resolvedDraftToken);

                navigate(
                    {
                        pathname: location.pathname,
                        search: `?${nextSearchParams.toString()}`,
                    },
                    { replace: true }
                );
            })
            .catch((error) => {
                if (isCancelled) {
                    return;
                }

                hydratedDraftTokenRef.current = "";
                setDraftToken("");
                setDraftLink("");
                setStepError(`${tr("bc.error.loadDraft")} ${tr(apiErrorKey(error))}`);
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoadingDraft(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [draftTokenFromSearch, location.pathname, location.search, navigate, tr]);

    const updateRegistrationField =
        (key: keyof RegistrationFormData) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const nextValue =
                event.target instanceof HTMLInputElement && event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value;
            const sanitizedValue =
                typeof nextValue === "string" && (key === "generalPhone" || key === "contactPhone")
                    ? sanitizePhoneInput(nextValue)
                    : nextValue;

            setRegistrationData((currentData) => ({
                ...currentData,
                [key]: sanitizedValue,
            }));
            clearValidationIssue(String(key));
        };

    const updateCaseField =
        (key: keyof CaseData) =>
        (value: string) => {
            setCaseData((currentData) => ({
                ...currentData,
                [key]: value,
            }));
            clearValidationIssue(String(key));
        };

    const syncRegistrationDataFromForm = () => {
        if (!registrationFormRef.current) {
            return registrationData;
        }

        const nextData = readRegistrationFormData(registrationFormRef.current, registrationData);
        setRegistrationData(nextData);
        return nextData;
    };

    const openFilePicker = () => {
        fileInputRef.current?.click();
    };

    const addFiles = (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) {
            return;
        }

        const validFiles: UploadedFileItem[] = [];

        Array.from(fileList).forEach((file) => {
            const extension = getFileExtension(file.name);

            if (!acceptedFileExtensions.includes(extension)) {
                return;
            }

            if (file.size > 30 * 1024 * 1024) {
                return;
            }

            validFiles.push({
                id: `${file.name}-${file.size}-${file.lastModified}`,
                file,
                name: file.name,
                size: file.size,
                extension,
            });
        });

        if (validFiles.length === 0) {
            setStepError(tr("bc.attachments.invalid"));
            return;
        }

        setUploadedFiles((currentFiles) => {
            const nextFiles = [...currentFiles];

            validFiles.forEach((file) => {
                if (!nextFiles.some((existingFile) => existingFile.id === file.id)) {
                    nextFiles.push(file);
                }
            });

            return nextFiles;
        });
        setStepError("");
        clearValidationIssue("attachments");
    };

    const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        addFiles(event.target.files);
        event.target.value = "";
    };

    const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setDragActive(false);
        addFiles(event.dataTransfer.files);
    };

    const removeFile = (fileId: string) => {
        setUploadedFiles((currentFiles) => currentFiles.filter((file) => file.id !== fileId));
    };

    const goToStep = (step: WizardStep) => {
        if (step > currentStep) {
            return;
        }

        setStepError("");
        setValidationIssues([]);
        syncWizardLocation(step, "replace");
    };

    const saveCurrentDraft = async () => {
        const nextRegistrationData = currentStep === 2 ? syncRegistrationDataFromForm() : registrationData;

        setIsSavingDraft(true);
        setDraftFeedback("");

        try {
            const draft = await saveBusinessCaseDraft({
                token: draftToken || undefined,
                currentStep,
                selectedCategoryId,
                registrationData: nextRegistrationData,
                caseData,
                uploadedFiles: uploadedFiles.map((uploadedFile) => ({
                    id: uploadedFile.id,
                    name: uploadedFile.name,
                    size: uploadedFile.size,
                    extension: uploadedFile.extension,
                    restored: uploadedFile.restored,
                })),
                submittedCases,
                backendRegistrationId: backendRegistrationId || null,
                submissionId: submissionId || null,
                submissionDate: submissionDate || null,
                draftCaseKey,
            });

            const nextDraftToken = draft.token ?? draftToken;

            if (!nextDraftToken) {
                throw new Error(tr("bc.draft.noLink"));
            }
            const nextDraftLink = buildDraftShareLink(nextDraftToken, location.pathname);

            hydratedDraftTokenRef.current = nextDraftToken;
            setDraftToken(nextDraftToken);
            setDraftLink(nextDraftLink);
            setStepError("");
            setIsDraftModalOpen(true);
            setDraftFeedback(tr("bc.draft.saved"));
            syncWizardLocation(currentStep, "replace", nextDraftToken);
        } catch (error) {
            setStepError(`${tr("bc.error.saveDraft")} ${tr(apiErrorKey(error))}`);
        } finally {
            setIsSavingDraft(false);
        }
    };

    const copyDraftLink = async () => {
        if (!draftLink) {
            return;
        }

        try {
            await navigator.clipboard.writeText(draftLink);
            setDraftFeedback(tr("bc.draft.copied"));
        } catch {
            setDraftFeedback(tr("bc.draft.copyFailed"));
        }
    };

    const handleNext = async () => {
        const nextRegistrationData = currentStep === 2 ? syncRegistrationDataFromForm() : registrationData;

        const nextValidationIssues = validateCurrentStep(
            currentStep,
            {
                selectedCategoryId,
                registrationData: nextRegistrationData,
                caseData,
                uploadedFiles,
                submittedCases,
            },
            tr
        );

        if (nextValidationIssues.length > 0) {
            setStepError("");
            setValidationIssues(nextValidationIssues);
            navigateToValidationIssue(nextValidationIssues[0]);

            return;
        }

        setStepError("");
        setValidationIssues([]);

        if (currentStep === 7) {
            const existingSubmittedCase = submittedCases.find((submittedCase) => submittedCase.draftCaseKey === draftCaseKey);

            if (existingSubmittedCase?.backendCaseId) {
                setSubmissionId(existingSubmittedCase.submissionId);
                setSubmissionDate(existingSubmittedCase.submittedAt);
                syncWizardLocation(8);
                return;
            }

            if (!captchaToken) {
                setStepError(tr("ui.err.captcha"));
                return;
            }

            setIsSubmitting(true);

            try {
                const nextBackendRegistrationId =
                    backendRegistrationId ||
                    (await createBusinessCaseRegistration(createRegistrationPayload(nextRegistrationData))).id;
                const createdBusinessCase = await createBusinessCase(
                    createBusinessCasePayload({
                        registrationId: nextBackendRegistrationId,
                        selectedCategoryId,
                        caseData,
                        uploadedFiles,
                    }),
                    captchaToken
                );
                const nextSubmissionDate = formatSubmissionDate(new Date(createdBusinessCase.createdAt));

                setBackendRegistrationId(nextBackendRegistrationId);
                setSubmissionId(createdBusinessCase.submissionNumber);
                setSubmissionDate(nextSubmissionDate);
                setSubmittedCases((currentCases) => [
                    ...currentCases.filter((submittedCase) => submittedCase.draftCaseKey !== draftCaseKey),
                    {
                        draftCaseKey,
                        backendCaseId: createdBusinessCase.id,
                        backendRegistrationId: nextBackendRegistrationId,
                        submissionId: createdBusinessCase.submissionNumber,
                        submittedAt: nextSubmissionDate,
                        categoryId: createdBusinessCase.category,
                        categoryTitle: selectedCategory.title,
                        caseTitle: createdBusinessCase.caseTitle,
                    },
                ]);
                syncWizardLocation(8);
            } catch (error) {
                setStepError(`${tr("bc.error.saveBusinessCase")} ${tr(apiErrorKey(error))}`);
                // Ein Turnstile-Token ist nur einmal einlösbar. Nach jedem
                // Fehlschlag muss das Widget ein frisches Token liefern.
                setCaptchaToken(null);
                setCaptchaReset((n) => n + 1);
            } finally {
                setIsSubmitting(false);
            }

            return;
        }

        syncWizardLocation((currentStep + 1) as WizardStep);
    };

    const handleBack = () => {
        setStepError("");
        setValidationIssues([]);

        if (currentStep === 1) {
            return;
        }

        syncWizardLocation((currentStep - 1) as WizardStep, "replace");
    };

    const resetFlow = () => {
        setSelectedCategoryId("campaign-of-the-year");
        setRegistrationData(emptyRegistrationData);
        setCaseData(emptyCaseData);
        setUploadedFiles([]);
        setDragActive(false);
        setStepError("");
        setValidationIssues([]);
        setSubmissionId("");
        setSubmissionDate("");
        setSubmittedCases([]);
        setBackendRegistrationId("");
        setDraftCaseKey(createDraftCaseKey());
        setDraftToken("");
        setDraftLink("");
        setDraftFeedback("");
        setIsDraftModalOpen(false);
        hydratedDraftTokenRef.current = "";
        clearStoredBusinessCaseWizardState();
        syncWizardLocation(1, "replace", null);
    };

    const startNextRegisteredCase = () => {
        const nextCategoryId =
            findNextAvailableCategoryId(registrationData, submittedCases, selectedCategoryId) ??
            selectedCategoryId;

        setSelectedCategoryId(nextCategoryId);
        setCaseData(emptyCaseData);
        setUploadedFiles([]);
        setDragActive(false);
        setStepError("");
        setSubmissionId("");
        setSubmissionDate("");
        setDraftCaseKey(createDraftCaseKey());
        syncWizardLocation(3);
    };

    return (
        <div id="business-case-page" className="default-page max">
            <nav className="business-case-nav" aria-label={tr("bc.nav.aria")}>                <Link to="/" className="business-case-nav-logo no-hover">
                    <img src={Logo} alt="TCG Awards" />
                </Link>

                <a className="business-case-home-button" href="/#top">
                    {tr("bc.nav.home")}
                </a>
            </nav>

            {currentStep <= 7 && (
                <section className="business-case-shell">
                    <div className="business-case-breadcrumbs">
                        <span>{tr("bc.nav.home")}</span>
                        <span>{tr("bc.nav.awards")}</span>
                        <span>{tr("bc.nav.submit")}</span>
                        <span>{tr("bc.nav.businessCase")}</span>
                    </div>

                    <div className="business-case-layout">
                        <aside className="business-case-sidebar">
                            <ol className="business-case-stepper">
                                {wizardSteps.map((step) => {
                                    const isActive = step.id === currentStep;
                                    const isCompleted = step.id < currentStep;

                                    return (
                                        <li
                                            key={step.id}
                                            className={`business-case-step-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                                        >
                                            <button
                                                type="button"
                                                className="business-case-step-button"
                                                onClick={() => goToStep(step.id)}
                                                disabled={step.id > currentStep}
                                            >
                                                <span className="business-case-step-number">{step.id}</span>
                                                <span className="business-case-step-text">
                                                    <strong>{step.title}</strong>
                                                    <small>{step.subtitle}</small>
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ol>

                            <div className="business-case-help-card">
                                <h3>{tr("bc.help.title")}</h3>
                                <p>{tr("bc.help.text")}</p>
                                <span className="business-case-help-note">
                                    {tr("bc.help.note")}
                                </span>
                            </div>
                        </aside>

                        <div className="business-case-panel">
                            {isLoadingDraft && (
                                <div className="business-case-summary-bar business-case-summary-bar-secondary">
                                    <span>{tr("bc.draft.status")}</span>
                                    <strong>{tr("bc.draft.loading")}</strong>
                                </div>
                            )}

                            {currentStep === 1 && (
                                <StepSection
                                    title={tr("bc.overview.title")}
                                    text={tr("bc.overview.text")}
                                >
                                    <div className="business-case-landing-cta">
                                        <p>{tr("bc.overview.ctaText")}</p>
                                        <button
                                            type="button"
                                            className="business-case-primary-button"
                                            onClick={() => syncWizardLocation(2)}
                                        >
                                            {tr("bc.overview.ctaButton")}
                                        </button>
                                    </div>

                                    <div className="business-case-guide-stack">
                                        {categories.map((category) => (
                                            <article key={category.id} className="business-case-guide-card landing">
                                                <div className="business-case-guide-intro">
                                                    <span className="business-case-guide-pill">{categoryTitle(category)}</span>
                                                    <p>{categoryDescription(category)}</p>
                                                </div>

                                                <div className="business-case-guide-timeline">
                                                    {category.sections.map((section) => (
                                                        <div key={`${category.id}-${section.title}`} className="business-case-guide-point">
                                                            <span className="business-case-guide-dot" aria-hidden="true" />
                                                            <div>
                                                                <strong>{guideSectionTitle(section)}</strong>
                                                                <p>{guideSectionDescription(section)}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </article>
                                        ))}
                                    </div>

                                    <div className="business-case-note-card business-case-evaluation-card">
                                        <strong>Evaluation Criteria</strong>
                                        <p>
                                            Not every text field must be filled with the same depth, but the jury should still be
                                            able to understand the scope, the strategic value, and the measurable impact of the
                                            case. Relevant metrics, supporting data, and well-structured attachments make the
                                            evaluation much easier.
                                        </p>
                                        <p>
                                            Optional supporting material such as decks, videos, links, screenshots, or one-pagers
                                            can strengthen the submission further. If you are not ready yet, save the form as a
                                            draft and continue later with your personal link.
                                        </p>
                                    </div>

                                    <div className="business-case-contact-row">
                                        <span>Questions?</span>
                                        <a href="mailto:cases@tcg-awards.com">cases@tcg-awards.com</a>
                                    </div>
                                </StepSection>
                            )}

                            {currentStep === 2 && (
                                <StepSection
                                    title={tr("bc.reg.step.title")}
                                    text={tr("bc.reg.step.text")}
                                >
                                    <div className="business-case-summary-bar">
                                        <span>{tr("bc.reg.selected")}</span>
                                        <strong>{totalCasesSelected} {tr("bc.common.total")}</strong>
                                    </div>

                                    {totalSubmittedCases > 0 && (
                                        <div className="business-case-summary-bar business-case-summary-bar-secondary">
                                            <span>{tr("bc.reg.alreadyPrepared")}</span>
                                            <strong>
                                                {totalSubmittedCases} {tr("bc.common.of")} {totalCasesSelected}
                                            </strong>
                                        </div>
                                    )}

                                    <form
                                        ref={registrationFormRef}
                                        className="business-case-registration-form"
                                        id="business-case-registration-top"
                                        noValidate
                                    >
                                        <div className="business-case-form-section-title business-case-field-full">
                                            {tr("bc.reg.sec.general")}
                                        </div>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.companyName")} *</span>
                                            <input
                                                type="text"
                                                name="companyName"
                                                value={registrationData.companyName}
                                                onChange={updateRegistrationField("companyName")}
                                                placeholder={tr("bc.reg.p.companyName")}
                                                autoComplete="organization"
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.vatId")}</span>
                                            <input
                                                type="text"
                                                name="vatId"
                                                value={registrationData.vatId}
                                                onChange={updateRegistrationField("vatId")}
                                                placeholder={tr("bc.reg.p.vatId")}
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.street")} *</span>
                                            <input
                                                type="text"
                                                name="streetAddress"
                                                value={registrationData.streetAddress}
                                                onChange={updateRegistrationField("streetAddress")}
                                                placeholder={tr("bc.reg.p.street")}
                                                autoComplete="street-address"
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.postcode")} *</span>
                                            <input
                                                type="text"
                                                name="postalCode"
                                                value={registrationData.postalCode}
                                                onChange={updateRegistrationField("postalCode")}
                                                placeholder={tr("bc.reg.p.postcode")}
                                                autoComplete="postal-code"
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.city")} *</span>
                                            <input
                                                type="text"
                                                name="city"
                                                value={registrationData.city}
                                                onChange={updateRegistrationField("city")}
                                                placeholder={tr("bc.reg.p.city")}
                                                autoComplete="address-level2"
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.country")} *</span>
                                            <select
                                                name="country"
                                                value={registrationData.country}
                                                onChange={updateRegistrationField("country")}
                                            >
                                                <option value="">{tr("bc.common.selectCountry")}</option>
                                                {countryOptions.map((country) => (
                                                    <option key={country} value={country}>
                                                        {countryLabel(country)}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.generalPhone")} *</span>
                                            <input
                                                type="tel"
                                                name="generalPhone"
                                                value={registrationData.generalPhone}
                                                onChange={updateRegistrationField("generalPhone")}
                                                placeholder="+49 30 12345678"
                                                autoComplete="tel"
                                                inputMode="tel"
                                                maxLength={30}
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.generalEmail")} *</span>
                                            <input
                                                type="email"
                                                name="generalEmail"
                                                value={registrationData.generalEmail}
                                                onChange={updateRegistrationField("generalEmail")}
                                                placeholder="office@company.com"
                                                autoComplete="email"
                                            />
                                        </label>

                                        <label className="business-case-field business-case-field-full">
                                            <span>{tr("bc.reg.f.diffBilling")} *</span>
                                            <select
                                                name="differentBillingAddress"
                                                value={registrationData.differentBillingAddress}
                                                onChange={updateRegistrationField("differentBillingAddress")}
                                            >
                                                <option value="">{tr("bc.common.pleaseChoose")}</option>
                                                <option value="no">{tr("bc.common.no")}</option>
                                                <option value="yes">{tr("bc.common.yes")}</option>
                                            </select>
                                        </label>

                                        {registrationData.differentBillingAddress === "yes" && (
                                            <>
                                                <div className="business-case-form-section-title business-case-field-full">
                                                    {tr("bc.reg.sec.billing")}
                                                </div>

                                                <label className="business-case-field">
                                                    <span>{tr("bc.reg.f.billingCompany")} *</span>
                                                    <input
                                                        type="text"
                                                        name="billingCompanyName"
                                                        value={registrationData.billingCompanyName}
                                                        onChange={updateRegistrationField("billingCompanyName")}
                                                        placeholder={tr("bc.reg.p.billingCompany")}
                                                    />
                                                </label>

                                                <label className="business-case-field">
                                                    <span>{tr("bc.reg.f.billingStreet")} *</span>
                                                    <input
                                                        type="text"
                                                        name="billingStreetAddress"
                                                        value={registrationData.billingStreetAddress}
                                                        onChange={updateRegistrationField("billingStreetAddress")}
                                                        placeholder={tr("bc.reg.p.billingStreet")}
                                                        autoComplete="street-address"
                                                    />
                                                </label>

                                                <label className="business-case-field">
                                                    <span>{tr("bc.reg.f.billingPostcode")} *</span>
                                                    <input
                                                        type="text"
                                                        name="billingPostalCode"
                                                        value={registrationData.billingPostalCode}
                                                        onChange={updateRegistrationField("billingPostalCode")}
                                                        placeholder={tr("bc.reg.p.billingPostcode")}
                                                        autoComplete="postal-code"
                                                    />
                                                </label>

                                                <label className="business-case-field">
                                                    <span>{tr("bc.reg.f.billingCity")} *</span>
                                                    <input
                                                        type="text"
                                                        name="billingCity"
                                                        value={registrationData.billingCity}
                                                        onChange={updateRegistrationField("billingCity")}
                                                        placeholder={tr("bc.reg.p.billingCity")}
                                                        autoComplete="address-level2"
                                                    />
                                                </label>

                                                <label className="business-case-field business-case-field-full">
                                                    <span>{tr("bc.reg.f.billingCountry")} *</span>
                                                    <select
                                                        name="billingCountry"
                                                        value={registrationData.billingCountry}
                                                        onChange={updateRegistrationField("billingCountry")}
                                                    >
                                                        <option value="">{tr("bc.common.selectCountry")}</option>
                                                        {countryOptions.map((country) => (
                                                            <option key={`billing-${country}`} value={country}>
                                                                {countryLabel(country)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            </>
                                        )}

                                        <div className="business-case-form-section-title business-case-field-full">
                                            {tr("bc.reg.sec.contact")}
                                        </div>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.contactFirst")} *</span>
                                            <input
                                                type="text"
                                                name="contactFirstName"
                                                value={registrationData.contactFirstName}
                                                onChange={updateRegistrationField("contactFirstName")}
                                                placeholder={tr("bc.reg.p.contactFirst")}
                                                autoComplete="given-name"
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.contactLast")} *</span>
                                            <input
                                                type="text"
                                                name="contactLastName"
                                                value={registrationData.contactLastName}
                                                onChange={updateRegistrationField("contactLastName")}
                                                placeholder={tr("bc.reg.p.contactLast")}
                                                autoComplete="family-name"
                                            />
                                        </label>

                                        <fieldset className="business-case-radio-group business-case-field-full">
                                            <legend>{tr("bc.reg.f.salutation")} *</legend>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="salutation"
                                                    value="female"
                                                    checked={registrationData.salutation === "female"}
                                                    onChange={updateRegistrationField("salutation")}
                                                />
                                                <span>{tr("bc.reg.sal.female")}</span>
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="salutation"
                                                    value="non-binary"
                                                    checked={registrationData.salutation === "non-binary"}
                                                    onChange={updateRegistrationField("salutation")}
                                                />
                                                <span>{tr("bc.reg.sal.nonbinary")}</span>
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="salutation"
                                                    value="male"
                                                    checked={registrationData.salutation === "male"}
                                                    onChange={updateRegistrationField("salutation")}
                                                />
                                                <span>{tr("bc.reg.sal.male")}</span>
                                            </label>
                                        </fieldset>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.contactPhone")} *</span>
                                            <input
                                                type="tel"
                                                name="contactPhone"
                                                value={registrationData.contactPhone}
                                                onChange={updateRegistrationField("contactPhone")}
                                                placeholder="+49 30 12345678"
                                                autoComplete="tel"
                                                inputMode="tel"
                                                maxLength={30}
                                            />
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.reg.f.contactEmail")} *</span>
                                            <input
                                                type="email"
                                                name="contactEmail"
                                                value={registrationData.contactEmail}
                                                onChange={updateRegistrationField("contactEmail")}
                                                placeholder="name@company.com"
                                                autoComplete="email"
                                            />
                                        </label>

                                        <div className="business-case-form-section-title business-case-field-full">
                                            {tr("bc.reg.sec.volume")}
                                        </div>

                                        <label className="business-case-field">
                                            <span>{tr("bc.cat.brand.title")}</span>
                                            <select
                                                name="brandPartnershipCount"
                                                value={registrationData.brandPartnershipCount}
                                                onChange={updateRegistrationField("brandPartnershipCount")}
                                            >
                                                {[0, 1, 2, 3].map((count) => (
                                                    <option key={`brand-${count}`} value={String(count)}>
                                                        {count} {tr(count === 1 ? "bc.common.case" : "bc.common.cases")}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="business-case-field">
                                            <span>{tr("bc.cat.campaign.title")}</span>
                                            <select
                                                name="campaignCount"
                                                value={registrationData.campaignCount}
                                                onChange={updateRegistrationField("campaignCount")}
                                            >
                                                {[0, 1, 2, 3].map((count) => (
                                                    <option key={`campaign-${count}`} value={String(count)}>
                                                        {count} {tr(count === 1 ? "bc.common.case" : "bc.common.cases")}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="business-case-field business-case-field-full">
                                            <span>{tr("bc.cat.service.title")}</span>
                                            <select
                                                name="serviceProviderCount"
                                                value={registrationData.serviceProviderCount}
                                                onChange={updateRegistrationField("serviceProviderCount")}
                                            >
                                                {[0, 1, 2, 3].map((count) => (
                                                    <option key={`service-${count}`} value={String(count)}>
                                                        {count} {tr(count === 1 ? "bc.common.case" : "bc.common.cases")}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                    </form>
                                </StepSection>
                            )}

                            {currentStep === 3 && (
                                <StepSection
                                    title={tr("bc.cat3.step.title")}
                                    text={tr("bc.cat3.step.text")}
                                >
                                    {selectedCategoryCount < 1 && (
                                        <div className="business-case-warning-banner">
                                            {tr("bc.cat3.warn.increase")}
                                        </div>
                                    )}

                                    {selectedCategoryCount > 0 && selectedCategoryRemainingCount < 1 && (
                                        <div className="business-case-warning-banner">
                                            {tr("bc.cat3.warn.allPrepared")}
                                        </div>
                                    )}

                                    <div
                                        className="business-case-category-grid"
                                        data-validation-field="category"
                                        tabIndex={-1}
                                    >
                                        {categories.map((category) => {
                                            const isSelected = selectedCategoryId === category.id;
                                            const categoryCount = getCategoryCaseCount(category.id, registrationData);
                                            const submittedCount = countSubmittedCasesForCategory(category.id, submittedCases);
                                            const remainingCount = Math.max(categoryCount - submittedCount, 0);
                                            const isUnavailable = remainingCount < 1;

                                            return (
                                                <button
                                                    type="button"
                                                    key={category.id}
                                                    className={`business-case-category-card ${isSelected ? "selected" : ""}`}
                                                    onClick={() => {
                                                        setSelectedCategoryId(category.id);
                                                        clearValidationIssue("category");
                                                    }}
                                                    disabled={isUnavailable}
                                                >
                                                    <div className="business-case-category-icon">
                                                        <CategoryIcon type={category.icon} />
                                                    </div>

                                                    <div className="business-case-category-copy">
                                                        <h3>{categoryTitle(category)}</h3>
                                                        <p>{categoryDescription(category)}</p>
                                                    </div>

                                                    <div className="business-case-category-footer">
                                                        <span>
                                                            {categoryCount < 1
                                                                ? tr("bc.cat3.notRegistered")
                                                                : `${remainingCount} ${tr("bc.cat3.remainingOf")} ${categoryCount}`}
                                                        </span>
                                                        <span className="business-case-category-radio" aria-hidden="true" />
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="business-case-note-card">
                                        <strong>{categoryTitle(selectedCategory)}</strong>
                                        <p>{categoryDescription(selectedCategory)}</p>
                                        <div className="business-case-guide-mini-grid">
                                            {selectedCategory.sections.map((section) => (
                                                <div key={section.title}>
                                                    <h4>{guideSectionTitle(section)}</h4>
                                                    <p>{guideSectionDescription(section)}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </StepSection>
                            )}

                            {currentStep === 4 && (
                                <StepSection
                                    title={tr("bc.case.step.title")}
                                    text={tr("bc.case.step.text")}
                                >
                                    <div className="business-case-process-card">
                                        <h2>{tr("bc.case.process.title")}</h2>
                                        <p>{tr("bc.case.process.p1")}</p>
                                        <p>{tr("bc.case.process.p2")}</p>
                                        <p>{tr("bc.case.process.p3")}</p>
                                        <p>{tr("bc.case.process.p4")}</p>
                                    </div>

                                    <div className="business-case-inline-guide">
                                        <strong>{tr("bc.case.selectedCategory")}</strong>
                                        <span>{categoryTitle(selectedCategory)}</span>
                                    </div>

                                    <div className="business-case-summary-bar business-case-summary-bar-secondary">
                                        <span>{tr("bc.case.remaining")}</span>
                                        <strong>{selectedCategoryRemainingCount}</strong>
                                    </div>

                                    <div className="business-case-form-grid single">
                                        <InputField
                                            name="title"
                                            label={tr("bc.case.f.title")}
                                            value={caseData.title}
                                            onChange={updateCaseField("title")}
                                            placeholder={tr("bc.case.p.title")}
                                            required
                                            maxLength={150}
                                            counter={`${caseData.title.length} / 150`}
                                        />
                                        <TextareaField
                                            name="shortDescription"
                                            label={tr("bc.case.f.shortDescription")}
                                            value={caseData.shortDescription}
                                            onChange={updateCaseField("shortDescription")}
                                            placeholder={tr("bc.case.p.shortDescription")}
                                            required
                                            maxLength={300}
                                            rows={4}
                                            counter={`${caseData.shortDescription.length} / 300`}
                                        />
                                        <TextareaField
                                            name="challenge"
                                            label={tr("bc.case.f.challenge")}
                                            value={caseData.challenge}
                                            onChange={updateCaseField("challenge")}
                                            placeholder={tr("bc.case.p.challenge")}
                                            required
                                            maxLength={1000}
                                            rows={4}
                                            counter={`${caseData.challenge.length} / 1000`}
                                        />
                                        <TextareaField
                                            name="objectives"
                                            label={tr("bc.case.f.objectives")}
                                            value={caseData.objectives}
                                            onChange={updateCaseField("objectives")}
                                            placeholder={tr("bc.case.p.objectives")}
                                            required
                                            maxLength={1000}
                                            rows={4}
                                            counter={`${caseData.objectives.length} / 1000`}
                                        />
                                        <TextareaField
                                            name="execution"
                                            label={tr("bc.case.f.execution")}
                                            value={caseData.execution}
                                            onChange={updateCaseField("execution")}
                                            placeholder={tr("bc.case.p.execution")}
                                            required
                                            maxLength={1000}
                                            rows={4}
                                            counter={`${caseData.execution.length} / 1000`}
                                        />
                                        <TextareaField
                                            name="impactMetrics"
                                            label={tr("bc.case.f.impactMetrics")}
                                            value={caseData.impactMetrics}
                                            onChange={updateCaseField("impactMetrics")}
                                            placeholder={tr("bc.case.p.impactMetrics")}
                                            required
                                            maxLength={1000}
                                            rows={4}
                                            counter={`${caseData.impactMetrics.length} / 1000`}
                                        />
                                        <TextareaField
                                            name="strategicValue"
                                            label={tr("bc.case.f.strategicValue")}
                                            value={caseData.strategicValue}
                                            onChange={updateCaseField("strategicValue")}
                                            placeholder={tr("bc.case.p.strategicValue")}
                                            required
                                            maxLength={1000}
                                            rows={4}
                                            counter={`${caseData.strategicValue.length} / 1000`}
                                        />
                                    </div>
                                </StepSection>
                            )}

                            {currentStep === 5 && (
                                <StepSection
                                    title={tr("bc.terms.step.title")}
                                    text={tr("bc.terms.step.text")}
                                >
                                    <div className="business-case-fee-card">
                                        <span>{tr("bc.terms.fee.label")}</span>
                                        <strong>{tr("bc.terms.fee.price")}</strong>
                                        <p>{tr("bc.terms.fee.note")}</p>
                                    </div>

                                    <div className="business-case-terms-stack">
                                        <article className="business-case-terms-card">
                                            <div className="business-case-terms-header">
                                                <h3>{tr("bc.terms.net.title")}</h3>
                                                <span>{tr("bc.terms.required")}</span>
                                            </div>
                                            <p>{tr("bc.terms.net.text")}</p>
                                            <label className="business-case-checkbox-card">
                                                <input
                                                    type="checkbox"
                                                    name="netPricesAccepted"
                                                    checked={registrationData.netPricesAccepted}
                                                    onChange={updateRegistrationField("netPricesAccepted")}
                                                />
                                                <div>
                                                    <strong>{tr("bc.terms.net.accept")}</strong>
                                                </div>
                                            </label>
                                        </article>

                                        <article className="business-case-terms-card">
                                            <div className="business-case-terms-header">
                                                <h3>{tr("bc.terms.conditions.title")}</h3>
                                                <span>{tr("bc.terms.required")}</span>
                                            </div>
                                            <p>{tr("bc.terms.conditions.text")}</p>
                                            <a
                                                className="business-case-terms-link"
                                                href="https://www.spiel-essen.de/en/"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {tr("bc.terms.conditions.link")}
                                            </a>
                                            <label className="business-case-checkbox-card">
                                                <input
                                                    type="checkbox"
                                                    name="conditionsAccepted"
                                                    checked={registrationData.conditionsAccepted}
                                                    onChange={updateRegistrationField("conditionsAccepted")}
                                                />
                                                <div>
                                                    <strong>{tr("bc.terms.conditions.accept")}</strong>
                                                </div>
                                            </label>
                                        </article>

                                        <article className="business-case-terms-card">
                                            <div className="business-case-terms-header">
                                                <h3>{tr("bc.terms.offer.title")}</h3>
                                                <span>{tr("bc.terms.required")}</span>
                                            </div>
                                            <p>{tr("bc.terms.offer.text")}</p>
                                            <div className="business-case-organizer-card">
                                                <strong>{tr("bc.terms.organizers.title")}</strong>
                                                <p>{tr("bc.terms.organizers.text")}</p>
                                                <p>
                                                    Merz Verlag GmbH &amp; Co. KG
                                                    <br />
                                                    Huyssenallee 105
                                                    <br />
                                                    45128 Essen, Germany
                                                    <br />
                                                    {tr("bc.terms.phone")}
                                                    <br />
                                                    E-Mail:{" "}
                                                    <a href="mailto:info@merz-verlag.com">
                                                        info@merz-verlag.com ({tr("bc.terms.openTab")})
                                                    </a>
                                                    <br />
                                                    <a href="https://www.spiel-essen.de/en/" target="_blank" rel="noreferrer">
                                                        www.spiel-essen.de ({tr("bc.terms.openTab")})
                                                    </a>
                                                </p>
                                                <p>
                                                    <strong>{tr("bc.terms.admin.title")}</strong>
                                                    <br />
                                                    {tr("bc.terms.admin.text")}
                                                </p>
                                            </div>
                                            <label className="business-case-checkbox-card">
                                                <input
                                                    type="checkbox"
                                                    name="legallyBindingOfferAccepted"
                                                    checked={registrationData.legallyBindingOfferAccepted}
                                                    onChange={updateRegistrationField("legallyBindingOfferAccepted")}
                                                />
                                                <div>
                                                    <strong>{tr("bc.terms.offer.accept")}</strong>
                                                </div>
                                            </label>
                                        </article>

                                        <article className="business-case-terms-card">
                                            <div className="business-case-terms-header">
                                                <h3>{tr("bc.terms.cancel.title")}</h3>
                                                <span>{tr("bc.terms.required")}</span>
                                            </div>
                                            <p>
                                                {tr("bc.terms.cancel.p1")}{" "}
                                                <a href="mailto:cases@tcg-awards.com">
                                                    cases@tcg-awards.com ({tr("bc.terms.openTab")})
                                                </a>
                                                . {tr("bc.terms.cancel.p2")}
                                            </p>
                                            <p>{tr("bc.terms.cancel.p3")}</p>
                                            <p>{tr("bc.terms.cancel.p4")}</p>
                                            <p>
                                                <strong>{tr("bc.terms.cancel.p5")}</strong>
                                            </p>
                                            <label className="business-case-checkbox-card">
                                                <input
                                                    type="checkbox"
                                                    name="cancellationAccepted"
                                                    checked={registrationData.cancellationAccepted}
                                                    onChange={updateRegistrationField("cancellationAccepted")}
                                                />
                                                <div>
                                                    <strong>{tr("bc.terms.cancel.accept")}</strong>
                                                </div>
                                            </label>
                                        </article>

                                        <article className="business-case-terms-card">
                                            <div className="business-case-terms-header">
                                                <h3>{tr("bc.terms.privacy.title")}</h3>
                                                <span>{tr("bc.terms.required")}</span>
                                            </div>
                                            <p>{tr("bc.terms.privacy.p1")}</p>
                                            <p>{tr("bc.terms.privacy.p2")}</p>
                                            <p>
                                                {tr("bc.terms.privacy.p3")}{" "}
                                                <a
                                                    href="https://www.spiel-essen.de/en/privacy"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    www.spiel-essen.de/en/privacy ({tr("bc.terms.openTab")})
                                                </a>
                                            </p>
                                            <label className="business-case-checkbox-card">
                                                <input
                                                    type="checkbox"
                                                    name="privacyAccepted"
                                                    checked={registrationData.privacyAccepted}
                                                    onChange={updateRegistrationField("privacyAccepted")}
                                                />
                                                <div>
                                                    <strong>{tr("bc.terms.privacy.accept")}</strong>
                                                </div>
                                            </label>
                                        </article>
                                    </div>
                                </StepSection>
                            )}

                            {currentStep === 6 && (
                                <StepSection
                                    title={tr("bc.attachments.step.title")}
                                    text={tr("bc.attachments.step.text")}
                                >
                                    <div className="business-case-form-grid single">
                                        <div className="business-case-upload-wrapper">
                                            <label className="business-case-upload-label">{tr("bc.attachments.uploadLabel")}</label>
                                            <button
                                                type="button"
                                                data-validation-field="attachments"
                                                className={`business-case-upload-dropzone ${dragActive ? "drag-active" : ""}`}
                                                onClick={openFilePicker}
                                                onDragEnter={(event) => {
                                                    event.preventDefault();
                                                    setDragActive(true);
                                                }}
                                                onDragOver={(event) => {
                                                    event.preventDefault();
                                                    setDragActive(true);
                                                }}
                                                onDragLeave={(event) => {
                                                    event.preventDefault();
                                                    setDragActive(false);
                                                }}
                                                onDrop={handleDrop}
                                            >
                                                <div className="business-case-upload-cloud">
                                                    <UploadIcon />
                                                </div>
                                                <strong>{tr("bc.attachments.dropzoneTitle")}</strong>
                                                <span>{tr("bc.attachments.dropzoneHelp")}</span>
                                            </button>

                                            <input
                                                ref={fileInputRef}
                                                className="business-case-hidden-input"
                                                type="file"
                                                multiple
                                                accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.mp4"
                                                onChange={handleFileInputChange}
                                            />
                                        </div>

                                        <div className="business-case-uploaded-files">
                                            <div className="business-case-uploaded-header">
                                                <h3>{tr("bc.attachments.uploadedTitle")}</h3>
                                                <span>
                                                    {uploadedFiles.length} {fileCountLabel(uploadedFiles.length)}
                                                </span>
                                            </div>

                                            {uploadedFiles.some((uploadedFile) => uploadedFile.restored) && (
                                                <div className="business-case-warning-banner">
                                                    {tr("bc.attachments.restoredWarning")}
                                                </div>
                                            )}

                                            {uploadedFiles.length === 0 && (
                                                <div className="business-case-upload-empty">{tr("bc.attachments.empty")}</div>
                                            )}

                                            {uploadedFiles.map((uploadedFile) => (
                                                <div className="business-case-upload-file" key={uploadedFile.id}>
                                                    <div className="business-case-upload-file-icon">
                                                        {uploadedFile.extension.toUpperCase()}
                                                    </div>

                                                    <div className="business-case-upload-file-copy">
                                                        <strong>{uploadedFile.name}</strong>
                                                        <span>
                                                            {uploadedFile.extension.toUpperCase()} -{" "}
                                                            {formatFileSize(uploadedFile.size)}
                                                        </span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="business-case-upload-remove"
                                                        onClick={() => removeFile(uploadedFile.id)}
                                                        aria-label={`${tr("bc.attachments.remove")} ${uploadedFile.name}`}
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </StepSection>
                            )}

                            {currentStep === 7 && (
                                <StepSection
                                    title={tr("bc.review.step.title")}
                                    text={tr("bc.review.step.text")}
                                >
                                    <div className="business-case-review-stack">
                                        <ReviewCard
                                            title={tr("bc.review.registration")}
                                            actionLabel={tr("bc.review.edit")}
                                            onAction={() => goToStep(2)}
                                            icon={<CompanyBadgeIcon />}
                                        >
                                            <strong>{registrationData.companyName || "-"}</strong>
                                            <span>{registrationData.streetAddress || tr("bc.review.noAddress")}</span>
                                            <span>
                                                {tr("bc.review.contact")}: {contactName || "-"} |{" "}
                                                {registrationData.contactEmail || "-"}
                                            </span>
                                            <span>
                                                {tr("bc.review.caseVolume")}: {selectedCaseBreakdown}
                                            </span>
                                            <span>
                                                {tr("bc.review.preparedSoFar")}: {totalSubmittedCases} {tr("bc.common.of")}{" "}
                                                {totalCasesSelected}
                                            </span>
                                        </ReviewCard>

                                        <ReviewCard
                                            title={tr("bc.review.category")}
                                            actionLabel={tr("bc.review.edit")}
                                            onAction={() => goToStep(3)}
                                            icon={<CategoryBadgeIcon />}
                                        >
                                            <strong>{categoryTitle(selectedCategory)}</strong>
                                            <p>{categoryDescription(selectedCategory)}</p>
                                        </ReviewCard>

                                        <ReviewCard
                                            title={tr("bc.review.businessCase")}
                                            actionLabel={tr("bc.review.edit")}
                                            onAction={() => goToStep(4)}
                                            icon={<CaseBadgeIcon />}
                                        >
                                            <strong>{caseData.title || "-"}</strong>
                                            <span>{tr("bc.case.f.shortDescription")}</span>
                                            <p>{caseData.shortDescription || tr("bc.review.noShortDescription")}</p>
                                            <span>{tr("bc.case.f.challenge")}</span>
                                            <p>{caseData.challenge || tr("bc.review.noChallenge")}</p>
                                            <span>{tr("bc.case.f.objectives")}</span>
                                            <p>{caseData.objectives || tr("bc.review.noObjectives")}</p>
                                            <span>{tr("bc.case.f.execution")}</span>
                                            <p>{caseData.execution || tr("bc.review.noExecution")}</p>
                                            <span>{tr("bc.case.f.impactMetrics")}</span>
                                            <p>{caseData.impactMetrics || tr("bc.review.noImpactMetrics")}</p>
                                            <span>{tr("bc.case.f.strategicValue")}</span>
                                            <p>{caseData.strategicValue || tr("bc.review.noStrategicValue")}</p>
                                        </ReviewCard>

                                        <ReviewCard
                                            title={tr("bc.terms.step.title")}
                                            actionLabel={tr("bc.review.edit")}
                                            onAction={() => goToStep(5)}
                                            icon={<TermsBadgeIcon />}
                                        >
                                            <strong>{tr("bc.review.feeNet")}</strong>
                                            <span>{tr("bc.review.termsAccepted")}</span>
                                        </ReviewCard>

                                        <ReviewCard
                                            title={tr("bc.attachments.step.title")}
                                            actionLabel={tr("bc.review.edit")}
                                            onAction={() => goToStep(6)}
                                            icon={<AttachmentBadgeIcon />}
                                        >
                                            <span>
                                                {uploadedFiles.length} {fileCountLabel(uploadedFiles.length)}{" "}
                                                {tr("bc.review.uploaded")}
                                            </span>
                                            <ul className="business-case-review-file-list">
                                                {uploadedFiles.map((file) => (
                                                    <li key={file.id}>{file.name}</li>
                                                ))}
                                            </ul>
                                        </ReviewCard>
                                    </div>
                                </StepSection>
                            )}

                            {validationIssues.length > 0 && (
                                <ValidationSummary issues={validationIssues} onSelect={navigateToValidationIssue} tr={tr} />
                            )}

                            {stepError && <div className="business-case-error-banner">{stepError}</div>}

                            {currentStep === 7 && (
                                <div className="business-case-captcha">
                                    <Turnstile
                                        onToken={setCaptchaToken}
                                        onExpire={() => setCaptchaToken(null)}
                                        resetSignal={captchaReset}
                                    />
                                </div>
                            )}

                            <div className="business-case-actions">
                                {currentStep === 1 ? (
                                    <Link to="/" className="business-case-secondary-button">
                                        {tr("bc.nav.backHome")}
                                    </Link>
                                ) : (
                                    <button
                                        type="button"
                                        className="business-case-secondary-button"
                                        onClick={handleBack}
                                        disabled={isSubmitting}
                                    >
                                        {tr("bc.nav.back")}
                                    </button>
                                )}

                                <div className="business-case-actions-group">
                                    {currentStep >= 2 && currentStep <= 7 && (
                                        <button
                                            type="button"
                                            className="business-case-secondary-button business-case-draft-button"
                                            onClick={saveCurrentDraft}
                                            disabled={isSubmitting || isSavingDraft || isLoadingDraft}
                                        >
                                            {isSavingDraft ? tr("bc.draft.saving") : tr("bc.draft.save")}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="business-case-primary-button"
                                        onClick={handleNext}
                                        disabled={isSubmitting || isLoadingDraft || (currentStep === 7 && !captchaToken)}  
                                        aria-busy={isSubmitting}
                                    >
                                        {isSubmitting && currentStep === 7 ? tr("bc.action.saving") : getPrimaryActionLabel(currentStep, tr)}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {isDraftModalOpen && (
                <div className="business-case-draft-modal-backdrop" role="presentation">
                    <div
                        className="business-case-draft-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="business-case-draft-title"
                    >
                        <button
                            type="button"
                            className="business-case-draft-modal-close"
                            onClick={() => setIsDraftModalOpen(false)}
                            aria-label={tr("bc.draft.close")}
                        >
                            ×
                        </button>

                        <h3 id="business-case-draft-title">{tr("bc.draft.title")}</h3>
                        <p>{tr("bc.draft.text")}</p>

                        <div className="business-case-draft-link-row">
                            <input type="text" value={draftLink} readOnly aria-label={tr("bc.draft.link")} />
                            <button type="button" onClick={copyDraftLink}>
                                {tr("bc.draft.copy")}
                            </button>
                        </div>

                        <span className="business-case-draft-modal-note">
                            {tr("bc.draft.note")}
                        </span>

                        {draftFeedback && <div className="business-case-draft-feedback">{draftFeedback}</div>}
                    </div>
                </div>
            )}

            {currentStep === 8 && (
                <section className="business-case-success-stage">
                    <div className="business-case-success-burst" aria-hidden="true">
                        {Array.from({ length: 20 }, (_, index) => (
                            <span
                                key={index}
                                style={{
                                    transform: `translate(-50%, -50%) rotate(${index * 18}deg) translateY(-178px) rotate(${-index * 18}deg)`,
                                }}
                            />
                        ))}
                    </div>

                    <div className="business-case-success-icon">
                        <SuccessCheckIcon />
                    </div>
                    <h1>{tr("bc.success.thanks")}</h1>
                    <h2>{tr("bc.success.title")}</h2>
                    <p>{tr("bc.success.text")}</p>

                    {remainingCases > 0 && (
                        <div className="business-case-note-card business-case-success-next-card">
                            <strong>{tr("bc.success.moreTitle")}</strong>
                            <p>
                                {tr("bc.success.registered")} {totalCasesSelected} {caseCountLabel(totalCasesSelected)}{" "}
                                {tr("bc.success.inTotal")} {totalSubmittedCases}. {tr("bc.success.remaining")}{" "}
                                {remainingCases} {caseCountLabel(remainingCases)} {tr("bc.success.toComplete")}
                            </p>
                        </div>
                    )}

                    <div className="business-case-success-card">
                        <h3>{tr("bc.success.details")}</h3>

                        <div className="business-case-success-row">
                            <span>{tr("bc.success.submissionNumber")}</span>
                            <strong>{submissionId}</strong>
                        </div>
                        <div className="business-case-success-row">
                            <span>{tr("bc.success.registrationId")}</span>
                            <strong>{backendRegistrationId}</strong>
                        </div>
                        <div className="business-case-success-row">
                            <span>{tr("bc.success.category")}</span>
                            <strong>{categoryTitle(selectedCategory)}</strong>
                        </div>
                        <div className="business-case-success-row">
                            <span>{tr("bc.success.company")}</span>
                            <strong>{registrationData.companyName}</strong>
                        </div>
                        <div className="business-case-success-row">
                            <span>{tr("bc.success.contactEmail")}</span>
                            <strong>{registrationData.contactEmail}</strong>
                        </div>
                        <div className="business-case-success-row">
                            <span>{tr("bc.success.submissionDate")}</span>
                            <strong>{submissionDate}</strong>
                        </div>
                        <div className="business-case-success-row">
                            <span>{tr("bc.success.progress")}</span>
                            <strong>
                                {totalSubmittedCases} / {totalCasesSelected} {tr("bc.success.prepared")}
                            </strong>
                        </div>
                    </div>

                    <div className="business-case-success-actions">
                        {remainingCases > 0 ? (
                            <button
                                type="button"
                                className="business-case-secondary-button full-width"
                                onClick={startNextRegisteredCase}
                            >
                                {tr("bc.success.createNext")}
                            </button>
                        ) : (
                            <button type="button" className="business-case-secondary-button full-width" onClick={resetFlow}>
                                {tr("bc.success.startAnother")}
                            </button>
                        )}
                        <Link to="/" className="business-case-primary-button full-width">
                            {tr("bc.success.returnHome")}
                        </Link>
                    </div>
                </section>
            )}
        </div>
    );
}

function StepSection(
    props: Readonly<{
        title: string;
        text: string;
        children: ReactNode;
    }>
) {
    return (
        <div className="business-case-step-section">
            <div className="business-case-section-intro">
                <h1>{props.title}</h1>
                <p>{props.text}</p>
            </div>
            {props.children}
        </div>
    );
}

function InputField(
    props: Readonly<{
        name: keyof CaseData;
        label: string;
        value: string;
        onChange: (value: string) => void;
        placeholder: string;
        required?: boolean;
        type?: string;
        maxLength?: number;
        counter?: string;
    }>
) {
    return (
        <label className="business-case-field">
            <span>
                {props.label}
                {props.required && <em>*</em>}
            </span>
            <div className="business-case-input-wrapper">
                <input
                    name={props.name}
                    type={props.type ?? "text"}
                    value={props.value}
                    maxLength={props.maxLength}
                    onChange={(event) => props.onChange(event.target.value)}
                    placeholder={props.placeholder}
                />
                {props.counter && <small>{props.counter}</small>}
            </div>
        </label>
    );
}

function TextareaField(
    props: Readonly<{
        name: keyof CaseData;
        label: string;
        value: string;
        onChange: (value: string) => void;
        placeholder: string;
        rows: number;
        required?: boolean;
        maxLength?: number;
        counter?: string;
    }>
) {
    return (
        <label className="business-case-field">
            <span>
                {props.label}
                {props.required && <em>*</em>}
            </span>
            <div className="business-case-input-wrapper textarea">
                <textarea
                    name={props.name}
                    value={props.value}
                    rows={props.rows}
                    maxLength={props.maxLength}
                    onChange={(event) => props.onChange(event.target.value)}
                    placeholder={props.placeholder}
                />
                {props.counter && <small>{props.counter}</small>}
            </div>
        </label>
    );
}

function ValidationSummary(
    props: Readonly<{
        issues: ValidationIssue[];
        onSelect: (issue: ValidationIssue) => void;
        tr: (key: string) => string;
    }>
) {
    return (
        <div className="business-case-error-banner business-case-validation-summary" role="alert">
            <strong>
                {props.tr("bc.validation.summaryStart")} {props.issues.length}{" "}
                {props.issues.length === 1 ? props.tr("bc.validation.error") : props.tr("bc.validation.errors")}{" "}
                {props.tr("bc.validation.summaryEnd")}
            </strong>
            <ul>
                {props.issues.map((issue) => (
                    <li key={`${issue.step}-${issue.field}-${issue.message}`}>
                        <button type="button" onClick={() => props.onSelect(issue)}>
                            {issue.message}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ReviewCard(
    props: Readonly<{
        title: string;
        actionLabel: string;
        onAction: () => void;
        icon: ReactNode;
        children: ReactNode;
    }>
) {
    return (
        <article className="business-case-review-card">
            <div className="business-case-review-card-header">
                <div className="business-case-review-title">
                    <span className="business-case-review-icon">{props.icon}</span>
                    <strong>{props.title}</strong>
                </div>
                <button type="button" onClick={props.onAction}>
                    {props.actionLabel}
                </button>
            </div>
            <div className="business-case-review-card-content">{props.children}</div>
        </article>
    );
}

function CategoryIcon(
    props: Readonly<{
        type: BusinessCategory["icon"];
    }>
) {
    const sharedProps = {
        width: 20,
        height: 20,
        viewBox: "0 0 20 20",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
    };

    switch (props.type) {
        case "campaign":
            return (
                <svg {...sharedProps}>
                    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6" />
                    <circle cx="10" cy="10" r="2.5" fill="currentColor" />
                </svg>
            );
        case "service":
            return (
                <svg {...sharedProps}>
                    <rect x="3" y="3" width="14" height="14" rx="2.8" stroke="currentColor" strokeWidth="1.6" />
                    <path
                        d="M6.5 13.5L9 11L11 13L14 9.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case "partnership":
        default:
            return (
                <svg {...sharedProps}>
                    <path
                        d="M6.4 11.4C7.6 10.5 8.7 9.6 10 8.8C11.3 9.6 12.4 10.5 13.6 11.4C14.8 12.3 15.4 13.4 15.4 14.8H4.6C4.6 13.4 5.2 12.3 6.4 11.4Z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                    />
                    <circle cx="7" cy="6.6" r="2.1" stroke="currentColor" strokeWidth="1.6" />
                    <circle cx="13" cy="6.6" r="2.1" stroke="currentColor" strokeWidth="1.6" />
                </svg>
            );
    }
}

function UploadIcon() {
    return (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12.5 31.7H29.2C34 31.7 37.5 28.5 37.5 24C37.5 19.9 34.6 17 30.7 16.5C29.8 11.2 25.5 7.3 20 7.3C14.3 7.3 9.8 11.6 9.2 17.1C5.2 17.7 2.5 20.6 2.5 24.6C2.5 28.8 5.8 31.7 10.2 31.7H12.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path d="M20 25.8V14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path
                d="M15.8 18.7L20 14.5L24.2 18.7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function TrashIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 5.3H14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M6.1 5.3V13.2C6.1 14.1 6.8 14.8 7.7 14.8H10.3C11.2 14.8 11.9 14.1 11.9 13.2V5.3" stroke="currentColor" strokeWidth="1.6" />
            <path d="M7 5V3.8C7 3.1 7.6 2.5 8.3 2.5H9.7C10.4 2.5 11 3.1 11 3.8V5" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    );
}

function CategoryBadgeIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 2.7L10.5 5.9L14 6.4L11.5 8.8L12.1 12.2L9 10.6L5.9 12.2L6.5 8.8L4 6.4L7.5 5.9L9 2.7Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    );
}

function CompanyBadgeIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.5 15V4.8L7.7 3V15" stroke="currentColor" strokeWidth="1.4" />
            <path d="M7.7 15V6.2L14.5 4.6V15" stroke="currentColor" strokeWidth="1.4" />
            <path d="M5.4 6.6H5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M10.2 7.4H10.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M12.6 7.4H12.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

function CaseBadgeIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="12" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.4" />
            <path d="M6 7.1H12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M6 9.7H12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M6 12.3H9.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
    );
}

function TermsBadgeIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3.4" y="2.8" width="11.2" height="12.4" rx="2" stroke="currentColor" strokeWidth="1.4" />
            <path d="M6.1 6.5H11.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M6.1 9H10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M6.1 11.5H9.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M11 12.1L12 13.1L14.2 10.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function AttachmentBadgeIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4.1" width="12" height="10.6" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
            <path d="M6.2 4.1V2.8H11.8V4.1" stroke="currentColor" strokeWidth="1.4" />
            <path d="M9 8V11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M7.5 9.5L9 8L10.5 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function SuccessCheckIcon() {
    return (
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="37" cy="37" r="34" stroke="currentColor" strokeWidth="3" />
            <path d="M24.5 37.6L33 46.1L49.5 27.9" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function getPrimaryActionLabel(currentStep: WizardStep, tr: (key: string) => string) {
    switch (currentStep) {
        case 1:
            return tr("bc.action.toRegistration");
        case 2:
            return tr("bc.action.toCategory");
        case 3:
            return tr("bc.action.toCase");
        case 4:
            return tr("bc.action.toTerms");
        case 5:
            return tr("bc.action.toAttachments");
        case 6:
            return tr("bc.action.toReview");
        case 7:
            return tr("bc.action.submit");
        default:
            return tr("bc.action.continue");
    }
}

function createRegistrationPayload(formData: RegistrationFormData): BusinessCaseRegistrationPayload {
    return {
        companyName: formData.companyName,
        vatId: formData.vatId,
        streetAddress: formData.streetAddress,
        postalCode: formData.postalCode,
        city: formData.city,
        country: formData.country,
        generalPhone: formData.generalPhone,
        generalEmail: formData.generalEmail,
        differentBillingAddress: formData.differentBillingAddress === "yes",
        billingCompanyName: formData.billingCompanyName,
        billingStreetAddress: formData.billingStreetAddress,
        billingPostalCode: formData.billingPostalCode,
        billingCity: formData.billingCity,
        billingCountry: formData.billingCountry,
        contactFirstName: formData.contactFirstName,
        contactLastName: formData.contactLastName,
        salutation: formData.salutation,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        brandPartnershipCount: Number(formData.brandPartnershipCount),
        campaignCount: Number(formData.campaignCount),
        serviceProviderCount: Number(formData.serviceProviderCount),
        netPricesAccepted: formData.netPricesAccepted,
        conditionsAccepted: formData.conditionsAccepted,
        legallyBindingOfferAccepted: formData.legallyBindingOfferAccepted,
        cancellationAccepted: formData.cancellationAccepted,
        privacyAccepted: formData.privacyAccepted,
    };
}

function createBusinessCasePayload(
    options: Readonly<{
        registrationId: string;
        selectedCategoryId: BusinessCaseCategoryId;
        caseData: CaseData;
        uploadedFiles: UploadedFileItem[];
    }>
): BusinessCasePayload {
    return {
        registrationId: options.registrationId,
        category: options.selectedCategoryId,
        caseTitle: options.caseData.title,
        shortDescription: options.caseData.shortDescription,
        challenge: options.caseData.challenge,
        objectives: options.caseData.objectives,
        execution: options.caseData.execution,
        impactMetrics: options.caseData.impactMetrics,
        strategicValue: options.caseData.strategicValue,
        attachments: options.uploadedFiles.map(createAttachmentPayload),
        files: options.uploadedFiles
            .map((uploadedFile) => uploadedFile.file)
            .filter((file): file is File => Boolean(file)),
    };
}

function createAttachmentPayload(uploadedFile: UploadedFileItem): BusinessCaseAttachmentPayload {
    return {
        fileName: uploadedFile.name,
        fileSize: uploadedFile.size,
        fileExtension: uploadedFile.extension,
        mimeType: uploadedFile.file?.type || null,
    };
}

function validateCurrentStep(
    step: WizardStep,
    data: Readonly<{
        selectedCategoryId: BusinessCaseCategoryId;
        registrationData: RegistrationFormData;
        caseData: CaseData;
        uploadedFiles: UploadedFileItem[];
        submittedCases?: SubmittedBusinessCase[];
    }>,
    tr: (key: string) => string
) {
    switch (step) {
        case 2:
            return validateRegistrationForm(data.registrationData, tr);
        case 3:
            return validateCategorySelection(
                data.selectedCategoryId,
                data.registrationData,
                data.submittedCases ?? [],
                tr
            );
        case 4:
            return validateCaseDescription(data.caseData, tr);
        case 5:
            return validateTermsAcceptance(data.registrationData, tr);
        case 6:
            return validateUploadedFilesForSubmit(data.uploadedFiles, "continue", tr);
        case 7:
            return [
                ...validateRegistrationForm(data.registrationData, tr),
                ...validateCategorySelection(
                    data.selectedCategoryId,
                    data.registrationData,
                    data.submittedCases ?? [],
                    tr
                ),
                ...validateCaseDescription(data.caseData, tr),
                ...validateTermsAcceptance(data.registrationData, tr),
                ...validateUploadedFilesForSubmit(data.uploadedFiles, "submit", tr),
            ];
        default:
            return [];
    }
}

function validateCategorySelection(
    selectedCategoryId: BusinessCaseCategoryId,
    registrationData: RegistrationFormData,
    submittedCases: SubmittedBusinessCase[],
    tr: (key: string) => string
): ValidationIssue[] {
    if (!selectedCategoryId) {
        return [
            {
                field: "category",
                message: tr("bc.validation.categoryRequired"),
                step: 3,
            },
        ];
    }

    const remainingCount = getRemainingCategoryCaseCount(selectedCategoryId, registrationData, submittedCases);

    if (remainingCount < 1) {
        return [
            {
                field: "category",
                message: tr("bc.validation.categoryRemaining"),
                step: 3,
            },
        ];
    }

    return [];
}

function validateUploadedFilesForSubmit(
    uploadedFiles: UploadedFileItem[],
    action: "continue" | "submit",
    tr: (key: string) => string
): ValidationIssue[] {
    if (uploadedFiles.length === 0) {
        const actionLabel = action === "submit" ? tr("bc.validation.action.submit") : tr("bc.validation.action.continue");

        return [
            {
                field: "attachments",
                message: `${tr("bc.validation.uploadBefore")} ${actionLabel}.`,
                step: 6,
            },
        ];
    }

    if (uploadedFiles.some((uploadedFile) => !uploadedFile.file || uploadedFile.restored)) {
        return [
            {
                field: "attachments",
                message: tr("bc.validation.reupload"),
                step: 6,
            },
        ];
    }

    return [];
}

function validateCaseDescription(caseData: CaseData, tr: (key: string) => string): ValidationIssue[] {
    const requiredFields: Array<{ field: keyof CaseData; label: string }> = [
        { field: "title", label: tr("bc.case.f.title") },
        { field: "shortDescription", label: tr("bc.case.f.shortDescription") },
        { field: "challenge", label: tr("bc.case.f.challenge") },
        { field: "objectives", label: tr("bc.case.f.objectives") },
        { field: "execution", label: tr("bc.case.f.execution") },
        { field: "impactMetrics", label: tr("bc.case.f.impactMetrics") },
        { field: "strategicValue", label: tr("bc.case.f.strategicValue") },
    ];

    return requiredFields
        .filter(({ field }) => !caseData[field].trim())
        .map(({ field, label }) => ({
            field,
            message: `${tr("bc.validation.requiredCaseField")} ${label}.`,
            step: 4,
        }));
}

function validateRegistrationForm(formData: RegistrationFormData, tr: (key: string) => string): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const requiredTextFields: Array<{ field: keyof RegistrationFormData; label: string }> = [
        { field: "companyName", label: tr("bc.reg.f.companyName") },
        { field: "streetAddress", label: tr("bc.reg.f.street") },
        { field: "postalCode", label: tr("bc.reg.f.postcode") },
        { field: "city", label: tr("bc.reg.f.city") },
        { field: "country", label: tr("bc.reg.f.country") },
        { field: "generalPhone", label: tr("bc.reg.f.generalPhone") },
        { field: "generalEmail", label: tr("bc.reg.f.generalEmail") },
        { field: "differentBillingAddress", label: tr("bc.reg.f.diffBilling") },
        { field: "contactFirstName", label: tr("bc.reg.f.contactFirst") },
        { field: "contactLastName", label: tr("bc.reg.f.contactLast") },
        { field: "salutation", label: tr("bc.reg.f.salutation") },
        { field: "contactPhone", label: tr("bc.reg.f.contactPhone") },
        { field: "contactEmail", label: tr("bc.reg.f.contactEmail") },
    ];

    requiredTextFields.forEach(({ field, label }) => {
        if (!String(formData[field]).trim()) {
            issues.push({ field, message: `${tr("bc.validation.requiredField")} ${label}.`, step: 2 });
        }
    });

    if (formData.generalEmail.trim() && !isValidEmail(formData.generalEmail)) {
        issues.push({
            field: "generalEmail",
            message: tr("bc.validation.validGeneralEmail"),
            step: 2,
        });
    }

    if (formData.contactEmail.trim() && !isValidEmail(formData.contactEmail)) {
        issues.push({
            field: "contactEmail",
            message: tr("bc.validation.validContactEmail"),
            step: 2,
        });
    }

    if (formData.generalPhone.trim() && !isValidPhoneNumber(formData.generalPhone)) {
        issues.push({
            field: "generalPhone",
            message: tr("bc.validation.validGeneralPhone"),
            step: 2,
        });
    }

    if (formData.contactPhone.trim() && !isValidPhoneNumber(formData.contactPhone)) {
        issues.push({
            field: "contactPhone",
            message: tr("bc.validation.validContactPhone"),
            step: 2,
        });
    }

    if (formData.differentBillingAddress === "yes") {
        const requiredBillingFields: Array<{ field: keyof RegistrationFormData; label: string }> = [
            { field: "billingCompanyName", label: tr("bc.reg.f.billingCompany") },
            { field: "billingStreetAddress", label: tr("bc.reg.f.billingStreet") },
            { field: "billingPostalCode", label: tr("bc.reg.f.billingPostcode") },
            { field: "billingCity", label: tr("bc.reg.f.billingCity") },
            { field: "billingCountry", label: tr("bc.reg.f.billingCountry") },
        ];

        requiredBillingFields.forEach(({ field, label }) => {
            if (!String(formData[field]).trim()) {
                issues.push({ field, message: `${tr("bc.validation.requiredField")} ${label}.`, step: 2 });
            }
        });
    }

    const totalCasesSelected =
        Number(formData.brandPartnershipCount) +
        Number(formData.campaignCount) +
        Number(formData.serviceProviderCount);

    if (totalCasesSelected < 1) {
        issues.push({
            field: "brandPartnershipCount",
            message: tr("bc.validation.atLeastOneCase"),
            step: 2,
        });
    }

    return issues;
}

function validateTermsAcceptance(formData: RegistrationFormData, tr: (key: string) => string): ValidationIssue[] {
    const confirmations: Array<{ field: keyof RegistrationFormData; message: string }> = [
        { field: "netPricesAccepted", message: tr("bc.validation.acceptNet") },
        { field: "conditionsAccepted", message: tr("bc.validation.acceptConditions") },
        {
            field: "legallyBindingOfferAccepted",
            message: tr("bc.validation.acceptOffer"),
        },
        { field: "cancellationAccepted", message: tr("bc.validation.acceptCancellation") },
        { field: "privacyAccepted", message: tr("bc.validation.acceptPrivacy") },
    ];

    return confirmations
        .filter(({ field }) => !formData[field])
        .map(({ field, message }) => ({ field, message, step: 5 }));
}

function sanitizePhoneInput(value: string) {
    return value.replace(/[^0-9+\-()./\s]/g, "");
}

function isValidPhoneNumber(value: string) {
    const trimmedValue = value.trim();
    const digitCount = (trimmedValue.match(/\d/g) ?? []).length;
    const plusCount = (trimmedValue.match(/\+/g) ?? []).length;

    return (
        /^[0-9+\-()./\s]+$/.test(trimmedValue) &&
        plusCount <= 1 &&
        (!trimmedValue.includes("+") || trimmedValue.startsWith("+")) &&
        digitCount >= 6 &&
        digitCount <= 15
    );
}

function isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function readRegistrationFormData(
    formElement: HTMLFormElement,
    currentFormData: RegistrationFormData
): RegistrationFormData {
    return {
        companyName: readTextField(formElement, "companyName", currentFormData.companyName),
        vatId: readTextField(formElement, "vatId", currentFormData.vatId),
        streetAddress: readTextField(formElement, "streetAddress", currentFormData.streetAddress),
        postalCode: readTextField(formElement, "postalCode", currentFormData.postalCode),
        city: readTextField(formElement, "city", currentFormData.city),
        country: readTextField(formElement, "country", currentFormData.country),
        generalPhone: readTextField(formElement, "generalPhone", currentFormData.generalPhone),
        generalEmail: readTextField(formElement, "generalEmail", currentFormData.generalEmail),
        differentBillingAddress: readTextField(
            formElement,
            "differentBillingAddress",
            currentFormData.differentBillingAddress
        ),
        billingCompanyName: readTextField(formElement, "billingCompanyName", currentFormData.billingCompanyName),
        billingStreetAddress: readTextField(formElement, "billingStreetAddress", currentFormData.billingStreetAddress),
        billingPostalCode: readTextField(formElement, "billingPostalCode", currentFormData.billingPostalCode),
        billingCity: readTextField(formElement, "billingCity", currentFormData.billingCity),
        billingCountry: readTextField(formElement, "billingCountry", currentFormData.billingCountry),
        contactFirstName: readTextField(formElement, "contactFirstName", currentFormData.contactFirstName),
        contactLastName: readTextField(formElement, "contactLastName", currentFormData.contactLastName),
        salutation: readTextField(formElement, "salutation", currentFormData.salutation),
        contactPhone: readTextField(formElement, "contactPhone", currentFormData.contactPhone),
        contactEmail: readTextField(formElement, "contactEmail", currentFormData.contactEmail),
        brandPartnershipCount: readTextField(
            formElement,
            "brandPartnershipCount",
            currentFormData.brandPartnershipCount
        ),
        campaignCount: readTextField(formElement, "campaignCount", currentFormData.campaignCount),
        serviceProviderCount: readTextField(
            formElement,
            "serviceProviderCount",
            currentFormData.serviceProviderCount
        ),
        netPricesAccepted: currentFormData.netPricesAccepted,
        conditionsAccepted: currentFormData.conditionsAccepted,
        legallyBindingOfferAccepted: currentFormData.legallyBindingOfferAccepted,
        cancellationAccepted: currentFormData.cancellationAccepted,
        privacyAccepted: currentFormData.privacyAccepted,
    };
}

function readTextField(formElement: HTMLFormElement, fieldName: string, fallbackValue: string) {
    const field = formElement.elements.namedItem(fieldName);

    if (field instanceof RadioNodeList) {
        return typeof field.value === "string" && field.value.length > 0 ? field.value : fallbackValue;
    }

    if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
    ) {
        return field.value;
    }

    return fallbackValue;
}

function getCategoryCaseCount(categoryId: BusinessCaseCategoryId, formData: RegistrationFormData) {
    if (categoryId === "brand-partnership") {
        return Number(formData.brandPartnershipCount);
    }

    if (categoryId === "campaign-of-the-year") {
        return Number(formData.campaignCount);
    }

    if (categoryId === "service-provider") {
        return Number(formData.serviceProviderCount);
    }

    return 0;
}

function countSubmittedCasesForCategory(categoryId: BusinessCaseCategoryId, submittedCases: SubmittedBusinessCase[]) {
    return submittedCases.filter((submittedCase) => submittedCase.categoryId === categoryId).length;
}

function getRemainingCategoryCaseCount(
    categoryId: BusinessCaseCategoryId,
    formData: RegistrationFormData,
    submittedCases: SubmittedBusinessCase[]
) {
    return Math.max(
        getCategoryCaseCount(categoryId, formData) - countSubmittedCasesForCategory(categoryId, submittedCases),
        0
    );
}

function findNextAvailableCategoryId(
    formData: RegistrationFormData,
    submittedCases: SubmittedBusinessCase[],
    preferredCategoryId: BusinessCaseCategoryId
) {
    if (getRemainingCategoryCaseCount(preferredCategoryId, formData, submittedCases) > 0) {
        return preferredCategoryId;
    }

    return categories.find((category) => getRemainingCategoryCaseCount(category.id, formData, submittedCases) > 0)?.id;
}

function getFileExtension(fileName: string) {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

function formatFileSize(sizeInBytes: number) {
    const sizeInMb = sizeInBytes / (1024 * 1024);
    return `${sizeInMb.toFixed(1)} MB`;
}

function formatSubmissionDate(date: Date) {
    return new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    }).format(date);
}

function readStoredBusinessCaseWizardState(): StoredBusinessCaseWizardState | null {
    if (typeof window === "undefined") {
        return null;
    }

    const rawState = window.sessionStorage.getItem(businessCaseStorageKey);

    if (!rawState) {
        return null;
    }

    try {
        return JSON.parse(rawState) as StoredBusinessCaseWizardState;
    } catch {
        return null;
    }
}

function writeStoredBusinessCaseWizardState(state: StoredBusinessCaseWizardState) {
    if (typeof window === "undefined") {
        return;
    }

    window.sessionStorage.setItem(businessCaseStorageKey, JSON.stringify(state));
}

function clearStoredBusinessCaseWizardState() {
    if (typeof window === "undefined") {
        return;
    }

    window.sessionStorage.removeItem(businessCaseStorageKey);
}

function parseStepFromSearch(search: string): WizardStep | null {
    const rawStep = new URLSearchParams(search).get("step");
    const parsedStep = Number(rawStep);

    if ([1, 2, 3, 4, 5, 6, 7, 8].includes(parsedStep)) {
        return parsedStep as WizardStep;
    }

    return null;
}

function parseDraftTokenFromSearch(search: string) {
    const rawToken = new URLSearchParams(search).get("draft");

    if (!rawToken || !/^[0-9a-f-]{36}$/i.test(rawToken)) {
        return null;
    }

    return rawToken;
}

function clampDraftStep(step: number): WizardStep {
    if ([1, 2, 3, 4, 5, 6, 7].includes(step)) {
        return step as WizardStep;
    }

    return 2;
}

function buildDraftShareLink(token: string, pathname: string) {
    if (typeof window === "undefined") {
        return `${pathname}?draft=${token}`;
    }

    return `${window.location.origin}${pathname}?draft=${token}`;
}

function normalizeDraftRegistrationData(registrationData: Record<string, unknown>): RegistrationFormData {
    return {
        companyName: readDraftString(registrationData, "companyName"),
        vatId: readDraftString(registrationData, "vatId"),
        streetAddress: readDraftString(registrationData, "streetAddress"),
        postalCode: readDraftString(registrationData, "postalCode"),
        city: readDraftString(registrationData, "city"),
        country: readDraftString(registrationData, "country"),
        generalPhone: readDraftString(registrationData, "generalPhone"),
        generalEmail: readDraftString(registrationData, "generalEmail"),
        differentBillingAddress: readDraftString(registrationData, "differentBillingAddress", "no"),
        billingCompanyName: readDraftString(registrationData, "billingCompanyName"),
        billingStreetAddress: readDraftString(registrationData, "billingStreetAddress"),
        billingPostalCode: readDraftString(registrationData, "billingPostalCode"),
        billingCity: readDraftString(registrationData, "billingCity"),
        billingCountry: readDraftString(registrationData, "billingCountry"),
        contactFirstName: readDraftString(registrationData, "contactFirstName"),
        contactLastName: readDraftString(registrationData, "contactLastName"),
        salutation: readDraftString(registrationData, "salutation"),
        contactPhone: readDraftString(registrationData, "contactPhone"),
        contactEmail: readDraftString(registrationData, "contactEmail"),
        brandPartnershipCount: readDraftCount(registrationData, "brandPartnershipCount"),
        campaignCount: readDraftCount(registrationData, "campaignCount"),
        serviceProviderCount: readDraftCount(registrationData, "serviceProviderCount"),
        netPricesAccepted: readDraftBoolean(registrationData, "netPricesAccepted"),
        conditionsAccepted: readDraftBoolean(registrationData, "conditionsAccepted"),
        legallyBindingOfferAccepted: readDraftBoolean(registrationData, "legallyBindingOfferAccepted"),
        cancellationAccepted: readDraftBoolean(registrationData, "cancellationAccepted"),
        privacyAccepted: readDraftBoolean(registrationData, "privacyAccepted"),
    };
}

function normalizeDraftCaseData(caseData: Record<string, unknown>): CaseData {
    return {
        title: readDraftString(caseData, "title"),
        shortDescription: readDraftString(caseData, "shortDescription"),
        challenge: readDraftString(caseData, "challenge"),
        objectives: readDraftString(caseData, "objectives"),
        execution: readDraftString(caseData, "execution"),
        impactMetrics: readDraftString(caseData, "impactMetrics"),
        strategicValue: readDraftString(caseData, "strategicValue"),
    };
}

function normalizeDraftUploadedFiles(uploadedFiles: BusinessCaseDraftState["uploadedFiles"]): UploadedFileItem[] {
    return uploadedFiles.map((uploadedFile, index) => ({
        id: readDraftString(uploadedFile, "id", `restored-${index}`),
        file: null,
        name: readDraftString(uploadedFile, "name", "Restored attachment"),
        size: readDraftNumber(uploadedFile, "size"),
        extension: readDraftString(uploadedFile, "extension"),
        restored: true,
    }));
}

function normalizeDraftSubmittedCases(submittedCases: BusinessCaseDraftState["submittedCases"]): SubmittedBusinessCase[] {
    return submittedCases
        .map((submittedCase) => ({
            draftCaseKey: readDraftString(submittedCase, "draftCaseKey"),
            backendCaseId: readDraftString(submittedCase, "backendCaseId"),
            backendRegistrationId: readDraftString(submittedCase, "backendRegistrationId"),
            submissionId: readDraftString(submittedCase, "submissionId"),
            submittedAt: readDraftString(submittedCase, "submittedAt"),
            categoryId: readDraftCategoryId(submittedCase, "categoryId"),
            categoryTitle: readDraftString(submittedCase, "categoryTitle"),
            caseTitle: readDraftString(submittedCase, "caseTitle"),
        }))
        .filter((submittedCase) => submittedCase.backendCaseId && submittedCase.submissionId);
}

function readDraftString(record: Record<string, unknown>, key: string, fallbackValue = "") {
    const value = record[key];

    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "number") {
        return String(value);
    }

    return fallbackValue;
}

function readDraftNumber(record: Record<string, unknown>, key: string, fallbackValue = 0) {
    const value = record[key];

    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsedNumber = Number(value);
        return Number.isFinite(parsedNumber) ? parsedNumber : fallbackValue;
    }

    return fallbackValue;
}

function readDraftCount(record: Record<string, unknown>, key: string) {
    return String(readDraftNumber(record, key, 0));
}

function readDraftBoolean(record: Record<string, unknown>, key: string, fallbackValue = false) {
    const value = record[key];

    if (typeof value === "boolean") {
        return value;
    }

    return fallbackValue;
}

function readDraftCategoryId(
    record: Record<string, unknown>,
    key: string
): BusinessCaseCategoryId {
    const value = record[key];

    if (
        value === "brand-partnership" ||
        value === "campaign-of-the-year" ||
        value === "service-provider"
    ) {
        return value;
    }

    return "campaign-of-the-year";
}

function createDraftCaseKey() {
    return `draft-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

export default BusinessCasePage;
